/**
 * QR Code Utilities for Certificate Verification (MEIWC, PAT, other certs)
 *
 * Wraps the `qrcode-generator` library into a matrix that @react-pdf/renderer
 * can draw as a module-by-module cell grid without raster images.
 * Uses Error Correction Level 'M' (15%) as standard for print QR codes.
 */

import qrcode from 'qrcode-generator';

export const QR_EC_LEVEL = 'M';

/**
 * Generate a QR boolean matrix (rows × cols of cell dark/light states).
 * @param {string|Object} payload - Text or object payload (object is JSON-stringified)
 * @param {number} typeNumber - QR version. 0 = auto-detect from payload size (recommended).
 * @param {string} ecLevel - Error correction level: L(7%), M(15%), Q(25%), H(30%)
 * @returns {Array<Array<boolean>>} matrix[row][col] where true = dark module
 */
export const generateQRMatrix = (payload, typeNumber = 0, ecLevel = QR_EC_LEVEL) => {
  const text = typeof payload === 'string' ? payload : JSON.stringify(payload);
  if (!text) throw new Error('QR payload cannot be empty');

  const qr = qrcode(typeNumber, ecLevel);
  qr.addData(text);
  qr.make();

  const size = qr.getModuleCount();
  const matrix = [];
  for (let row = 0; row < size; row++) {
    const cells = [];
    for (let col = 0; col < size; col++) {
      cells.push(qr.isDark(row, col));
    }
    matrix.push(cells);
  }
  return matrix;
};

/**
 * Render a QR matrix as an SVG fragment (data URI) for use in <img> or
 * react-pdf Image components that accept data URLs.
 * @param {Array<Array<boolean>>} matrix - Matrix from generateQRMatrix
 * @param {number} moduleSize - Scalar multiplier for each module (default 4)
 * @returns {string} data:image/svg+xml;utf8,... URI
 */
export const qrMatrixToDataURL = (matrix, moduleSize = 4) => {
  const size = matrix.length;
  const dim = size * moduleSize;
  let svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${dim}" height="${dim}" viewBox="0 0 ${dim} ${dim}">`;
  svg += `<rect width="${dim}" height="${dim}" fill="#FFFFFF"/>`;
  for (let row = 0; row < size; row++) {
    for (let col = 0; col < size; col++) {
      if (matrix[row][col]) {
        svg += `<rect x="${col * moduleSize}" y="${row * moduleSize}" width="${moduleSize}" height="${moduleSize}" fill="#000000"/>`;
      }
    }
  }
  svg += '</svg>';
  return 'data:image/svg+xml;utf8,' + encodeURIComponent(svg);
};

export default generateQRMatrix;